<template>
  <div class="tab-container project-settings">
    <div v-if="!isDataFetched">
      <div class="project-settings__no-data">Awaiting Data</div>
    </div>
    <template v-else>
      <div class="project-settings__table-wrapper">
        <div v-for="row in tableConfig"
             :key="row.property"
             class="project-settings__table-row">
          <div class="project-settings__table-row__title">{{row.label}}</div>
          <template v-if="row.isSelectable">
            <base-select :class="['project-settings__select',
                                  'project-settings__table-row__field',
                                  {'project-settings__table-row__field_err': errorFields.includes(row.property),
                                    'project-settings__field_disabled': disableButtons || row.isReadOnly
                                  }]"
                         :options="selectOptions[row.property]"
                         alignLeft
                         :isSmartInput="row.isSmartInput"
                         :isSortAsc="true"
                         borderLess
                         :disabled="disableButtons || row.isReadOnly"
                         v-model="modelValues[row.property]"
                         placeholder="Select option"/>
          </template>
          <template v-else>
            <input v-model="modelValues[row.property]"
                   :class="['project-settings__table-row__field',
                            {
                              'project-settings__table-row__field_err': errorFields.includes(row.property),
                              'project-settings__field_disabled': disableButtons || row.isReadOnly
                            }]"
                   placeholder="n/a"
                   :disabled="disableButtons || row.isReadOnly"
                   :type="['msv_reporting_minimum', 'reporting_lookback_months'].includes(row.property) ? 'number' : 'text'"
                   :min="numberInputMinimum(row.property)"
                   >
          </template>
        </div>
      </div>
    </template>

    <div class="project-settings__update-loader" v-if="isDataUpdating">
      <base-loader class="project-settings__update-loader-ring"/>
    </div>

    <div class="project-settings__footer">
      <button v-if="isDataFetched"
              :class="['project-settings__footer__button', {'project-settings__field_disabled': disableButtons}]"
              @click="saveChanges">save changes</button>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'

const DEFAULT_CONFIG = [
  {
    label: 'Difficulty Score Enabled',
    property: 'difficulty_score_enabled',
    isSmartInput: false,
    isSelectable: true
  },
  {
    label: 'Allow URL Case-Sensitivity',
    property: 'url_normalization_enabled',
    isSmartInput: false,
    isSelectable: true
  },
  {
    label: 'Auto-Update URL HTTP Status',
    property: 'automated_http_status_update',
    isSmartInput: false,
    isSelectable: true
  },
  {
    label: 'MSV 1 Label',
    property: 'msv_1',
    isSelectable: false
  },
  {
    label: 'MSV 2 Label',
    property: 'msv_2',
    isSelectable: false
  },
  {
    label: 'MSV 3 Label',
    property: 'msv_3',
    isSelectable: false
  },
  {
    label: 'MSV 4 Label',
    property: 'msv_4',
    isSelectable: false
  },
]

const yesNoOptions = [
  {
    'label': 'No',
    'value': 1
  },
  {
    'label': 'Yes',
    'value': 0
  }
]

const DEFAULT_MODEL_VALUES = {
  msv_1: '',
  msv_2: '',
  msv_3: '',
  msv_4: '',
  difficulty_score_enabled: null,
  url_normalization_enabled: null,
  automated_http_status_update: null,
}

export default {
  name: 'Strategy',
  data () {
    return {
      isDataFetched: false,
      isDataUpdating: false,
      disableButtons: false,
      modelValues: { ...DEFAULT_MODEL_VALUES },
      errorFields: []
    }
  },
  watch: {
    clientId: {
      handler (clientId) {
        clientId && this.loadData()
      },
      immediate: true
    },
    projectData: {
      handler (v) {
        Object.assign(this.modelValues, v)
      },
      deep: true,
      immediate: true
    }
  },
  computed: {
    ...mapState('projectSettings', {
      selectOptions (state) {
        return {
          difficulty_score_enabled: state.isActiveOptions,
          url_normalization_enabled: yesNoOptions,
          automated_http_status_update: state.isActiveOptions
        }
      },
      'projectData': 'projectData'
    }),
    isUserSuperAdmin () {
      return this.userRoles.includes('admin')
    },
    tableConfig () {
      return [...DEFAULT_CONFIG].map(el => {
        if ([].includes(el.property)) { // for all EXCEPT these
          el.isReadOnly = !this.isUserSuperAdmin // Set to readonly if user is not admin
        }
        return el
      })
    },
  },
  methods: {
    ...mapActions('projectSettings', ['getProjectSettings', 'changeProjectSettings']),
    ...mapActions(['getCurrentSession', 'updateSelectedClientData']),
    async loadData () {
      if (Object.keys(this.projectData).length) {
        this.isDataUpdating = false
        this.isDataFetched = true
      } else {
        this.isDataUpdating = true
        await this.getProjectSettings(this.clientId)
        this.isDataUpdating = false
        this.isDataFetched = true
      }
    },
    numberInputMinimum (val) {
      if (val === 'msv_reporting_minimum') {
        return 5
      }
      if (val === 'reporting_lookback_months') {
        return 1
      }
      return false
    },
    async saveChanges () {
      const selectProperties = ['difficulty_score_enabled', 'automated_http_status_update']
      const modelValues = { ...this.modelValues }
      const errorFields = selectProperties.filter(prop => !this.selectOptions[prop].some(i => i.value === modelValues[prop]))
      if (!modelValues.account_name || !modelValues.account_name.length) {
        errorFields.push('account_name')
      }
      if (modelValues.msv_reporting_minimum < 0) {
        errorFields.push('msv_reporting_minimum')
      }

      this.errorFields = errorFields
      if (errorFields.length) {
        return
      }

      this.disableButtons = true
      this.isDataUpdating = true
      const isSuccessfully = await this.changeProjectSettings(
        {
          clientId: this.clientId,
          ...modelValues
        }
      )
      // if (isSuccessfully) {
      //   this.isDataUpdating = true
      //   await this.getCurrentSession()
      //   const client = this.clients.find(item => item._id === this.clientId)
      //   this.updateSelectedClientData(client)
      // }
      this.isDataUpdating = false

      this.disableButtons = false
      this.loadData()
    }
  }
}
</script>

<style scoped lang="scss">
  .tab-container {
    background-color: #fff;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }
  .project-settings {
    padding-top: 14px;
    position: relative;
    min-height: 50vh !important;
    padding: 50px 100px;

    &__tabs {
      display: flex;
      margin-bottom: -1px;

      &_btn {
        margin-left: auto;
      }
      &_tab {
        width: max-content;
        padding: 16px 9px;
        text-align: center;
        font-size: 14px;
        color: $rfx-light-march;
        background-color: transparent;
        border: 1px solid transparent;
        text-decoration: none;

        &_error {
          color: $rfx-red;
        }

        &_active {
          background-color: #fff;
          color: $rfx-dark-march;
          font-weight: bold;
          border: 1px solid $rfx-light-steel;
          border-bottom: 1px solid #fff;
        }
      }
    }


    &__title-left {
      display: flex;
      align-items: center;
    }

    &__title-left > *:not(:last-child) {
      margin-right: 30px;
    }

    &__title-container {
      padding: 14px 0;
      display: flex;
      align-items: center;
    }

    &__no-data {
      display: flex;
      height: 300px;
      background-color: #fff;
      border-radius: $rfx-default-border-radius;
      align-items: center;
      justify-content: center;
      font-size: 27px;
      font-weight: 600;
      color: $rfx-medium-grey;
      flex: 1 1;
    }

    &__update-loader {
      position: absolute;
      display: flex;
      width: 100%;
      height: 100%;
      justify-content: center;
      background-color: white;
      top: 0;
      opacity: 0.5;
      z-index: 10000;
    }

    &__update-loader-ring {
      margin-top: 200px;
    }

    &__table-wrapper {
      width: 100%;
      display: flex;
      flex-direction: column;
      border-top: 1px solid $rfx-light-steel;
      border-left: 1px solid $rfx-light-steel;
      border-right: 1px solid $rfx-light-steel;
      border-radius: $rfx-default-border-radius;
    }

    &__table-row {
      display: flex;
      width: 100%;
      background-color: #fff;
      border-bottom: 1px solid $rfx-light-steel;

      &:first-child {
        border-top-left-radius: $rfx-default-border-radius;
        border-top-right-radius: $rfx-default-border-radius;

        input {
          border-top-right-radius: $rfx-default-border-radius;
        }
      }

      &:last-child {
        border-bottom-left-radius: $rfx-default-border-radius;
        border-bottom-right-radius: $rfx-default-border-radius;
      }

      &__title {
        color: $rfx-dark-march;
        flex: 1;
        font-weight: bold;
        padding: 15px 0 15px 25px;
        font-size: 14px;
        border-right: 1px solid $rfx-light-steel;
      }

      &__field {
        flex: 2;
        padding: 15px 0 15px 25px;
        border: 1px solid transparent;

        &_err {
          border: 1px solid $rfx-light-red !important;
        }

        &::placeholder {
          color: $rfx-dark-march;
        }
      }
    }

    &__select {
      padding: 3px 0 3px 25px !important;
      height: 60px !important;

      &::v-deep .select__input {
        position: relative;
        left: -22px;
        padding-left: 22px;
      }

      &::v-deep .select__input-container {
        background-color: #fff;
        margin-left: -4px;
      }

    }

    &__field_disabled {
      pointer-events: none !important;
      background-color: $rfx-grey;
      color: $rfx-dark-grey;
    }

    &__footer {
      margin-top: 40px;
      display: flex;
      align-items: center;
      justify-content: flex-end;

      &__button {
        min-width: 100px;
        padding: 0 25px;
        height: 40px;
        font-size: 10px;
        font-weight: 900;
        cursor: pointer;
        text-transform: uppercase;
        border-radius: 8px;
        outline: none;
        border: none;
        color: #fff;
        background-color: $rfx-dark-march;
      }
    }
  }
</style>
